import { useMutation } from "@apollo/client";
import { compact, omit } from "lodash";
import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import {
  PerformanceAssessmentChartLabelInput,
  SavePerfAssessmentLabelMutation,
  SavePerfAssessmentLabelMutationVariables,
} from "types/graphql-schema";
import { WithUUID } from "types/topicflow";
import { v4 as uuidv4 } from "uuid";

import createOrUpdatePerfAssessmentLabelMutation from "@apps/reporting/graphql/create-or-update-perf-assessment-label-mutation";
import { currentOrganizationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Input from "@components/input/input";
import Modal, { ModalWidth } from "@components/modal/modal";

import { getNineBoxChartLabel } from "./nine-box/helpers";
import NineBoxGrid, {
  SingleQuestionNineBoxGrid,
} from "./nine-box/nine-box-grid";
import { ChartLabel, NineBoxQuestion } from "./nine-box/types";

const PerformanceAssessmentChartEditLabelsModal = ({
  xQuestion,
  yQuestion,
  chartLabels,
  onClose,
  onChartLabelsSaved,
}: {
  onClose: () => void;
  onChartLabelsSaved: (chartLabels: ChartLabel[]) => void;
  xQuestion: NineBoxQuestion;
  yQuestion?: NineBoxQuestion | null;
  chartLabels: ChartLabel[];
}) => {
  const organization = currentOrganizationVar();
  const [proposedChartLabels, setProposedChartLabels] = useState<
    WithUUID<PerformanceAssessmentChartLabelInput>[]
  >(
    chartLabels.map((label) => ({
      id: label.id,
      questionOneId: label.questionOne.id,
      questionTwoId: label.questionTwo?.id,
      questionOneIntegerAnswer: label.questionOneIntegerAnswer,
      questionTwoIntegerAnswer: label.questionTwoIntegerAnswer,
      label: label.label,
      uuid: uuidv4(),
    }))
  );

  const [saveChartLabels, { loading: isSaving }] = useMutation<
    SavePerfAssessmentLabelMutation,
    SavePerfAssessmentLabelMutationVariables
  >(createOrUpdatePerfAssessmentLabelMutation);

  const handleSaveLabels = useCallback(() => {
    saveChartLabels({
      variables: {
        organizationId: organization.id,
        labels: proposedChartLabels.map((label) => omit(label, "uuid")),
      },
      onCompleted: (data) => {
        const newLabels = data.createOrUpdatePerformanceAssessmentChartLabel
          .performanceAssessmentChartLabels
          ? compact([
              ...data.createOrUpdatePerformanceAssessmentChartLabel
                .performanceAssessmentChartLabels,
            ])
          : [];
        onChartLabelsSaved(newLabels);
        onClose();
      },
    });
  }, [
    onChartLabelsSaved,
    onClose,
    organization.id,
    proposedChartLabels,
    saveChartLabels,
  ]);

  return createPortal(
    <Modal width={ModalWidth.medium} open onClose={onClose}>
      <div className="p-8 font-bold text-xl">Edit Cell Labels</div>
      <div className="p-8 flex justify-center">
        {xQuestion && yQuestion && (
          <NineBoxGrid
            xQuestion={xQuestion}
            yQuestion={yQuestion}
            cellRenderer={({ xValue, yValue }) => {
              const label = getNineBoxChartLabel(
                proposedChartLabels,
                xQuestion.id,
                xValue,
                yQuestion?.id,
                yValue
              );

              return (
                <div className="p-4 bg-gray-100 rounded-md flex items-center justify-center">
                  <Input
                    value={label?.label ?? ""}
                    onChange={(e) => {
                      if (!label) {
                        setProposedChartLabels((prev) => [
                          ...prev,
                          {
                            questionOneId: xQuestion.id,
                            questionOneIntegerAnswer: xValue,
                            questionTwoId: yQuestion?.id,
                            questionTwoIntegerAnswer: yValue,
                            uuid: uuidv4(),
                            label: e.target.value,
                          },
                        ]);
                      } else {
                        setProposedChartLabels((prev) =>
                          prev.map((prevLabel) => {
                            if (
                              "uuid" in label &&
                              prevLabel.uuid === label.uuid
                            ) {
                              return {
                                ...prevLabel,
                                label: e.target.value,
                              };
                            }
                            return prevLabel;
                          })
                        );
                      }
                    }}
                  />
                </div>
              );
            }}
          />
        )}
        {xQuestion && !yQuestion && (
          <SingleQuestionNineBoxGrid
            question={xQuestion}
            cellRenderer={({ value }) => {
              const label = getNineBoxChartLabel(
                proposedChartLabels,
                xQuestion.id,
                value
              );

              return (
                <div className="p-4 bg-gray-100 rounded-md flex items-center justify-center">
                  <Input
                    value={label?.label ?? ""}
                    onChange={(e) => {
                      if (!label) {
                        setProposedChartLabels((prev) => [
                          ...prev,
                          {
                            questionOneId: xQuestion.id,
                            questionOneIntegerAnswer: value,
                            uuid: uuidv4(),
                            label: e.target.value,
                          },
                        ]);
                      } else {
                        setProposedChartLabels((prev) =>
                          prev.map((prevLabel) => {
                            if (
                              "uuid" in label &&
                              prevLabel.uuid === label.uuid
                            ) {
                              return {
                                ...prevLabel,
                                label: e.target.value,
                              };
                            }
                            return prevLabel;
                          })
                        );
                      }
                    }}
                  />
                </div>
              );
            }}
          />
        )}
      </div>
      <div className="flex items-center justify-end">
        <div className="p-8 flex justify-end items-center gap-4">
          <Button
            theme={buttonTheme.text}
            text="Cancel"
            onClick={onClose}
            disabled={isSaving}
          />
          <Button
            theme={buttonTheme.primary}
            text="Save"
            onClick={handleSaveLabels}
            disabled={isSaving}
          />
        </div>
      </div>
    </Modal>,
    document.body
  );
};

export default PerformanceAssessmentChartEditLabelsModal;
